/**
 * A Stimulus controller that automatically adjusts textarea height based on content.
 * Extends stimulus-textarea-autogrow to provide custom minimum height behavior and optional maximum height.
 *
 * @example
 * <textarea data-controller="autogrow"></textarea>
 * <textarea data-controller="autogrow" data-autogrow-min-height-value="50"></textarea>
 * <textarea data-controller="autogrow" data-autogrow-max-height-value="200"></textarea>
 * <textarea data-controller="autogrow" data-autogrow-max-height-value="50vh"></textarea>
 * <textarea data-controller="autogrow" data-autogrow-min-height-value="50" data-autogrow-max-height-value="300"></textarea>
 *
 * @extends TextareaAutogrow
 */
import TextareaAutogrow from 'stimulus-textarea-autogrow'

export default class extends TextareaAutogrow {
  static values = {
    minHeight: { type: Number, default: 70 },
    // Accepts px (e.g., "300" or "300px"), vh/vw (e.g., "50vh"), rem/em (e.g., "20rem").
    // No cap by default unless a value is provided.
    maxHeight: { type: String, default: '' }
  }

  connect () {
    if (super.connect) super.connect()

    // Defer autogrow until the element is actually visible (e.g., when tab becomes active)
    this._setupVisibilityObserver()

    // Also recalc when viewport resizes because max height depends on viewport height
    this._onWindowResize = () => this.autogrow()
    window.addEventListener('resize', this._onWindowResize, { passive: true })
  }

  disconnect () {
    if (super.disconnect) super.disconnect()
    this._teardownVisibilityObserver()
    if (this._onWindowResize) {
      window.removeEventListener('resize', this._onWindowResize)
      this._onWindowResize = null
    }
  }

  /**
   * Adjusts the textarea height based on its content, respecting min height.
   * If `maxHeightValue` is provided (> 0), caps the height to that value; otherwise, no cap is applied.
   * @private
   */
  autogrow () {
    // Determine effective min height
    const minHeight = this.minHeightValue; // Default is handled by Stimulus

    // Reset height to auto to get the natural scrollHeight
    this.element.style.height = 'auto';
    const scrollHeight = this.element.scrollHeight;

    let newHeight = Math.max(minHeight, scrollHeight);

    const maxHeightPx = this._computeMaxHeightPx()
    if (maxHeightPx && newHeight > maxHeightPx) {
      this.element.style.height = `${maxHeightPx}px`;
      this.element.style.overflowY = 'auto';
    } else {
      this.element.style.height = `${newHeight}px`;
      this.element.style.overflowY = 'hidden';
    }
  }

  // Private
  _setupVisibilityObserver () {
    // If already visible, apply immediately
    if (this._isVisible()) {
      this.autogrow()
      return
    }

    if (!('IntersectionObserver' in window)) return

    this._intersectionObserver = new IntersectionObserver((entries) => {
      const entry = entries[0]
      if (entry && entry.isIntersecting) {
        this.autogrow()
        this._teardownVisibilityObserver()
      }
    }, { threshold: 0.01 })

    this._intersectionObserver.observe(this.element)
  }

  _teardownVisibilityObserver () {
    if (this._intersectionObserver) {
      this._intersectionObserver.disconnect()
      this._intersectionObserver = null
    }
  }

  _isVisible () {
    const rects = this.element.getClientRects()
    return rects.length > 0 && this.element.offsetParent !== null
  }

  _computeMaxHeightPx () {
    if (!this.hasMaxHeightValue || !this.maxHeightValue) return null

    const raw = String(this.maxHeightValue).trim().toLowerCase()
    if (!raw) return null

    // Numeric without unit or with px
    if (/^\d+(\.\d+)?(px)?$/.test(raw)) {
      const n = parseFloat(raw)
      return isFinite(n) && n > 0 ? Math.round(n) : null
    }

    // vh
    const vhMatch = raw.match(/^(\d+(?:\.\d+)?)vh$/)
    if (vhMatch) {
      const ratio = parseFloat(vhMatch[1]) / 100
      return Math.max(1, Math.round(window.innerHeight * ratio))
    }

    // vw
    const vwMatch = raw.match(/^(\d+(?:\.\d+)?)vw$/)
    if (vwMatch) {
      const ratio = parseFloat(vwMatch[1]) / 100
      return Math.max(1, Math.round(window.innerWidth * ratio))
    }

    // rem
    const remMatch = raw.match(/^(\d+(?:\.\d+)?)rem$/)
    if (remMatch) {
      const rem = parseFloat(remMatch[1])
      const rootFontPx = parseFloat(getComputedStyle(document.documentElement).fontSize)
      return Math.max(1, Math.round(rem * rootFontPx))
    }

    // em
    const emMatch = raw.match(/^(\d+(?:\.\d+)?)em$/)
    if (emMatch) {
      const em = parseFloat(emMatch[1])
      const fontPx = parseFloat(getComputedStyle(this.element).fontSize)
      return Math.max(1, Math.round(em * fontPx))
    }

    // Unsupported unit (e.g., %) → ignore and apply no cap
    return null
  }
}
